import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';

const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/curso-imagen`;

const EMPTY = {
  idInstructor: '',
  nombre: '',
  idNivel: '',
  duracion: '',
  idContenido: 0,
  idTipoHabilidad: '',
  uRLRutaImagen: '',
  autoAsignable: false,
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  instructores = [],
  niveles = [],
  tiposHabilidad = [],
  empleados = [],
  personas = [],
  onClose,
  onSubmit,
  inline = false,
}) {
  const { getSystemConfig } = useLogos();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState(EMPTY);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previousImagePath, setPreviousImagePath] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, uRLRutaImagen: 'Solo se permiten archivos de imagen.' }));
      return;
    }

    const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
    if (file.size > maxMB * 1024 * 1024) {
      setErrors(prev => ({ ...prev, uRLRutaImagen: `La imagen no debe superar los ${maxMB}MB.` }));
      return;
    }

    setSelectedFile(file);

    const blobUrl = URL.createObjectURL(file);
    setForm(prev => ({ ...prev, uRLRutaImagenPreview: blobUrl }));

    try {
      setIsUploading(true);
      if (previousImagePath) {
        try {
          await fetch(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`, {
            method: 'DELETE',
          });
        } catch (deleteError) {
          console.warn('No se pudo eliminar la imagen anterior:', deleteError);
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(UPLOAD_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al subir la imagen');
      }

      const data = await response.json();
      if (!data.filePath) {
        throw new Error('La respuesta del servidor no incluye filePath.');
      }

      setPreviousImagePath(data.filePath);

      setForm(prev => ({
        ...prev,
        uRLRutaImagen: data.filePath,
        uRLRutaImagenPreview: blobUrl,
      }));

      setErrors(prev => ({ ...prev, uRLRutaImagen: '' }));
    } catch (error) {
      console.error('Error al subir la imagen del curso:', error);
      setErrors(prev => ({
        ...prev,
        uRLRutaImagen: error.message || 'Error al subir la imagen al servidor.',
      }));

      setForm(prev => ({ ...prev, uRLRutaImagenPreview: null }));
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const availableInstructores = useMemo(() => {
    return instructores.map(i => {
      const emp = empleados.find(e => e.idEmpleado === i.idEmpleado);
      if (!emp) return { idInstructor: i.idInstructor, nombre: `Instructor ${i.idInstructor}` };
      const p = personas.find(p => p.idPersona === emp.idPersona);
      const nombre = p ? `${p.nombre} ${p.apellidoPaterno} ${p.apellidoMaterno || ''}`.trim() : `Empleado ${emp.idEmpleado}`;
      return { idInstructor: i.idInstructor, nombre };
    }).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [instructores, empleados, personas]);

  const availableNiveles = useMemo(() => {
    return niveles.sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [niveles]);

  const availableTiposHabilidad = useMemo(() => {
    return tiposHabilidad.filter(t => t.activo).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [tiposHabilidad]);

  const validateField = (field, value) => {
    let error = '';
    if (!value) {
      error = 'Este campo es obligatorio.';
    }
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateAll = () => {
    const newErrors = {};
    let isValid = true;
    // Validar todos los campos excepto idContenido
    Object.keys(EMPTY).forEach(key => {
      if (key === 'idContenido') return; // Saltar validación de idContenido
      if (!form[key]) {
        newErrors[key] = 'Este campo es obligatorio.';
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filledForm = {
        idCurso: data.idCurso ?? 0,
        idInstructor: data.idInstructor ?? '',
        nombre: data.nombre ?? '',
        idNivel: data.idNivel ?? '',
        duracion: data.duracion ?? '',
        idContenido: data.idContenido ?? 0,
        idTipoHabilidad: data.idTipoHabilidad ?? '',
        uRLRutaImagen: data.uRLRutaImagen ?? '',
        autoAsignable: data.autoAsignable ?? false,
      };
      setForm(filledForm);
      if (filledForm.uRLRutaImagen) {
        setPreviousImagePath(filledForm.uRLRutaImagen);
      }
      // Validate pre-filled data (excepto idContenido que no es obligatorio)
      Object.keys(filledForm).forEach(key => {
        if (key in EMPTY && key !== 'idContenido') {
          validateField(key, filledForm[key]);
        }
      });
    } else {
      setForm(EMPTY);
      setErrors(EMPTY);
      setSelectedFile(null);
      setPreviousImagePath(null);
    }
  }, [mode, data]);

  if (!open) return null;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo curso' : 'Editar curso'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Instructor: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <select
          className={`infoField${errors.idInstructor ? ' infoField--error' : ''}`}
          value={form.idInstructor}
          onChange={(e) => handleChange('idInstructor', Number(e.target.value) || '')}
        >
          <option value="">-- Selecciona un instructor --</option>
          {availableInstructores.map(i => (
            <option key={i.idInstructor} value={i.idInstructor}>{i.nombre}</option>
          ))}
        </select>
        {errors.idInstructor && <small style={errorStyle}>{errors.idInstructor}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre del curso: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.nombre ? ' infoField--error' : ''}`}
          value={form.nombre}
          onChange={(e) => handleChange('nombre', e.target.value)}
          maxLength={200}
        />
        {errors.nombre && <small style={errorStyle}>{errors.nombre}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nivel: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <select
          className={`infoField${errors.idNivel ? ' infoField--error' : ''}`}
          value={form.idNivel}
          onChange={(e) => handleChange('idNivel', Number(e.target.value) || '')}
        >
          <option value="">-- Selecciona un nivel --</option>
          {availableNiveles.map(n => (
            <option key={n.idNivel} value={n.idNivel}>{n.nombre}</option>
          ))}
        </select>
        {errors.idNivel && <small style={errorStyle}>{errors.idNivel}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Duración (días): <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.duracion ? ' infoField--error' : ''}`}
          type="number"
          min={0}
          step="any"
          value={form.duracion}
          onChange={(e) => handleChange('duracion', Number(e.target.value) || '')}
        />
        {errors.duracion && <small style={errorStyle}>{errors.duracion}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Tipo de Habilidad:</label>
        <br /><br />
        <select
          className={`infoField${errors.idTipoHabilidad ? ' infoField--error' : ''}`}
          value={form.idTipoHabilidad}
          onChange={(e) => handleChange('idTipoHabilidad', Number(e.target.value) || '')}
        >
          <option value="">-- Selecciona un tipo --</option>
          {availableTiposHabilidad.map(t => (
            <option key={t.idTipoHabilidad} value={t.idTipoHabilidad}>{t.nombre}</option>
          ))}
        </select>
        {errors.idTipoHabilidad && <small style={errorStyle}>{errors.idTipoHabilidad}</small>}
      </div>

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        padding: '12px 16px', 
        backgroundColor: '#f8fafc', 
        borderRadius: '12px', 
        border: '1px solid #e2e8f0',
        gridColumn: '1 / 3' // Ocupar todo el ancho para que se vea mejor el switch
      }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="autoAsignable" className="roles-title" style={{ fontSize: '.95rem', cursor: 'pointer', marginBottom: '2px' }}>
            Libre inscripción
          </label>
          <span style={{ fontSize: '.75rem', color: '#64748b' }}>Permite que los alumnos se inscriban por sí mismos a este curso.</span>
        </div>
        <div
          onClick={() => setForm(prev => ({ ...prev, autoAsignable: !form.autoAsignable }))}
          style={{
            width: '48px',
            height: '24px',
            backgroundColor: form.autoAsignable ? '#10b981' : '#cbd5e1',
            borderRadius: '12px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
          }}
        >
          <div style={{
            width: '18px',
            height: '18px',
            backgroundColor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: '3px',
            left: form.autoAsignable ? '27px' : '3px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }} />
        </div>
      </div>



      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Imagen del curso: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {/* Área de carga con preview integrado */}
          <div
            className={`file-uploader${form.uRLRutaImagen ? ' file-uploader--filled' : ''}${errors.uRLRutaImagen ? ' file-uploader--error' : ''}${isUploading ? ' file-uploader--uploading' : ''}`}
          >
            {/* Badge de estado */}
            {form.uRLRutaImagen && (
              <div className="file-uploader__badge">
                <span>✓</span>
                <span>Archivo cargado</span>
              </div>
            )}

            {/* Vista previa si existe */}
            {form.uRLRutaImagen && (
              <div className="file-uploader__preview">
                <img
                  src={form.uRLRutaImagenPreview || (form.uRLRutaImagen?.startsWith('http') ? form.uRLRutaImagen : `${SERVER_URL}${form.uRLRutaImagen}`)}
                  alt="Vista previa del curso"
                />
              </div>
            )}

            {/* Botón de carga */}
            <label
              className="file-uploader__label"
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div className="file-uploader__icon">
                {form.uRLRutaImagen ? '🔄' : '☁️'}
              </div>
              <div className="file-uploader__text">
                {form.uRLRutaImagen ? 'Cambiar imagen' : 'Haz clic para subir imagen'}
              </div>
              <div className="file-uploader__hint">
                JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
              </div>
            </label>
          </div>

          {errors.uRLRutaImagen && <small style={errorStyle}>{errors.uRLRutaImagen}</small>}
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="btn-cancel" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) {
            onSubmit(form, mode);
          }
        }}
        disabled={
          isUploading ||
          !form.idInstructor ||
          !form.nombre ||
          !form.idNivel ||
          !form.duracion ||
          !form.uRLRutaImagen ||
          Object.values(errors).some(x => x)
        }
        style={{
          opacity: (isUploading || !form.idInstructor || !form.nombre || !form.idNivel || !form.duracion || !form.uRLRutaImagen || Object.values(errors).some(x => x)) ? 0.5 : 1,
          cursor: (isUploading || !form.idInstructor || !form.nombre || !form.idNivel || !form.duracion || !form.uRLRutaImagen || Object.values(errors).some(x => x)) ? 'not-allowed' : 'pointer',
        }}
      >
        {isUploading ? 'Subiendo...' : (mode === 'new' ? 'Guardar' : 'Actualizar')}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
